/**
 * SoundManager — sons procedurais via Web Audio API (sem arquivos de áudio)
 * Chamado pela primeira vez após interação do usuário, para respeitar
 * a política de autoplay dos browsers.
 */
export class SoundManager {
  private ctx:        AudioContext | null = null
  private masterGain: GainNode    | null = null
  private volume = 0.8  // 0-1

  setVolume(pct: number): void {
    this.volume = Math.max(0, Math.min(1, pct / 100))
    if (this.masterGain) this.masterGain.gain.value = this.volume
  }

  private getCtx(): AudioContext {
    if (!this.ctx) {
      try {
        this.ctx        = new AudioContext()
        this.masterGain = this.ctx.createGain()
        this.masterGain.gain.value = this.volume
        this.masterGain.connect(this.ctx.destination)
      } catch { return null as never }
    }
    if (this.ctx.state === 'suspended') this.ctx.resume()
    return this.ctx
  }

  private dest(): AudioNode {
    return this.masterGain ?? this.getCtx().destination
  }

  /** Toque curto ao explodirem grupos de blocos */
  playPop(groupCount = 1): void {
    const ctx = this.getCtx()
    const t   = ctx.currentTime
    const n   = Math.min(groupCount, 5)
    for (let i = 0; i < n; i++) {
      const osc  = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain); gain.connect(this.dest())
      osc.type = 'triangle'
      const f = 520 + i * 90
      osc.frequency.setValueAtTime(f, t + i * 0.04)
      osc.frequency.exponentialRampToValueAtTime(f * 1.6, t + i * 0.04 + 0.07)
      gain.gain.setValueAtTime(0.11, t + i * 0.04)
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.04 + 0.13)
      osc.start(t + i * 0.04); osc.stop(t + i * 0.04 + 0.15)
    }
  }

  /** Beep ascendente para cada onda de chain */
  playChain(count: number): void {
    const ctx    = this.getCtx()
    const t      = ctx.currentTime
    const base   = 330 * Math.pow(1.18, Math.min(count - 2, 6))
    ;[base, base * 1.25, base * 1.5].forEach((f, i) => {
      const osc  = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain); gain.connect(this.dest())
      osc.type = 'square'
      osc.frequency.setValueAtTime(f, t + i * 0.08)
      gain.gain.setValueAtTime(0.07, t + i * 0.08)
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.08 + 0.18)
      osc.start(t + i * 0.08); osc.stop(t + i * 0.08 + 0.2)
    })
  }

  /** Baque grave ao receber lixo */
  playGarbageReceived(amount: number): void {
    const ctx = this.getCtx()
    const t   = ctx.currentTime
    const vol = 0.08 + Math.min(1, amount / 15) * 0.12
    const osc  = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain); gain.connect(this.dest())
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(90, t)
    osc.frequency.exponentialRampToValueAtTime(35, t + 0.18)
    gain.gain.setValueAtTime(vol, t)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.22)
    osc.start(t); osc.stop(t + 0.25)
  }

  /** Fanfarra de vitória */
  playWin(): void {
    const ctx = this.getCtx()
    const t   = ctx.currentTime
    ;[440, 550, 660, 880, 1100].forEach((f, i) => {
      const osc  = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain); gain.connect(this.dest())
      osc.type = 'triangle'
      osc.frequency.setValueAtTime(f, t + i * 0.09)
      gain.gain.setValueAtTime(0.14, t + i * 0.09)
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.09 + 0.35)
      osc.start(t + i * 0.09); osc.stop(t + i * 0.09 + 0.38)
    })
  }

  /**
   * Chame a partir de um handler de gesto do usuário (keydown/pointerdown) para
   * desbloquear o AudioContext antes do primeiro som.
   * Browsers mantêm o contexto em "suspended" até uma interação direta do usuário.
   */
  unlock(): void {
    if (!this.ctx) {
      try { this.ctx = new AudioContext() } catch { /* sem suporte */ }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {})
    }
  }

  /** Som de derrota */
  playLose(): void {
    const ctx = this.getCtx()
    const t   = ctx.currentTime
    ;[440, 370, 277, 220].forEach((f, i) => {
      const osc  = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain); gain.connect(this.dest())
      osc.type = 'sawtooth'
      osc.frequency.setValueAtTime(f, t + i * 0.13)
      gain.gain.setValueAtTime(0.09, t + i * 0.13)
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.13 + 0.28)
      osc.start(t + i * 0.13); osc.stop(t + i * 0.13 + 0.32)
    })
  }
}
